<template>
  <section class="question-explanation-preview">
    <v-row wrap>
      <v-col v-if="hasQuestionSpecificExplanation || adminEditMode" cols="12">
        <div class="question-explanation-preview__header">
          <v-btn
            v-if="adminEditMode"
            type="button"
            class="mb-3"
            color="primary"
            density="comfortable"
            prepend-icon="mdi-pencil"
            size="small"
            variant="tonal"
            @click="emit('edit-section', 'specific-explanation')"
          >
            Edit specific article
          </v-btn>
        </div>
        <div
          v-if="hasQuestionSpecificExplanation"
          class="px-6 rounded-lg bg-slightBlue noselect"
        >
          <div
            style="min-height: unset"
            class="tiptap bg-slightBlue v-html-content"
          >
            <div v-html="sanitizedQuestionSpecificExplanationHtml"></div>
          </div>
        </div>
      </v-col>

      <v-col cols="12">
        <div class="question-explanation-preview__header">
          <v-btn
            v-if="adminEditMode"
            type="button"
            class="mb-3"
            color="primary"
            density="comfortable"
            prepend-icon="mdi-pencil"
            size="small"
            variant="tonal"
            @click="emit('edit-section', 'explanation')"
          >
            Edit article
          </v-btn>
        </div>
        <div class="px-6 py-5 rounded-lg bg-slightBlue noselect">
          <div class="tiptap bg-slightBlue">
            <ExplanationViewer
              :explanation-h-t-m-l="answerExplanationHtml"
              :answer-title="answerTitle"
              :universal-images="universalImages"
              :universal-images-cols="universalImagesCols"
              :universal-image-captions="universalImageCaptions"
              :answer-images="answerImages"
              :answer-image-cols="answerImageCols"
              :answer-image-captions="answerImageCaptions"
              :answer-image-location="answerImageLocation"
              :all-img-paths="allImgPaths"
              :last-updated-display="lastUpdatedDisplay"
            >
              <template #rating>
                <slot name="rating"></slot>
              </template>
            </ExplanationViewer>
          </div>
        </div>
      </v-col>
    </v-row>
  </section>
</template>

<script setup>
import { computed } from "vue";

import ExplanationViewer from "@/components/ExplanationViewer.vue";
import { sanitizeExplanationHtml } from "@/utils/explanationHtmlSanitizer";

const props = defineProps({
  questionSpecificExplanationHtml: {
    type: String,
    default: "",
  },
  answerExplanationHtml: {
    type: String,
    default: "",
  },
  answerTitle: {
    type: String,
    default: "",
  },
  universalImages: {
    type: Array,
    default: () => [],
  },
  universalImagesCols: {
    type: Number,
    default: 1,
  },
  universalImageCaptions: {
    type: Object,
    default: () => ({}),
  },
  answerImages: {
    type: Array,
    default: () => [],
  },
  answerImageCols: {
    type: Number,
    default: 1,
  },
  answerImageCaptions: {
    type: Object,
    default: () => ({}),
  },
  answerImageLocation: {
    type: String,
    default: "bottom",
  },
  allImgPaths: {
    type: Object,
    required: true,
  },
  lastUpdatedDisplay: {
    type: Object,
    default: null,
  },
  adminEditMode: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["edit-section"]);

const sanitizedQuestionSpecificExplanationHtml = computed(() =>
  sanitizeExplanationHtml(props.questionSpecificExplanationHtml || ""),
);

const hasQuestionSpecificExplanation = computed(() => {
  const htmlContent = sanitizedQuestionSpecificExplanationHtml.value;
  if (typeof htmlContent !== "string") return false;

  const stripped = htmlContent
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/gi, "")
    .replace(/\u00a0/g, "")
    .trim();

  return stripped.length > 0;
});
</script>
